import { createServerSideHelpers } from "@trpc/react-query/server";
import type { GetStaticPaths, GetStaticPropsContext } from "next";
import superjson from "superjson";

import { OFFICIAL_SORCERY_TCG_ACCOUNT_EMAIL } from "@sorcery/db";

import { env } from "~/env";
import { prisma } from "~/server/db/client";
import { appRouter } from "~/server/trpc/root";
import { createTRPCContext } from "~/server/trpc/trpc";

export const getStaticProps = async (context: GetStaticPropsContext<{ deckId: string }>) => {
  // create ssg helpers
  const ssg = createServerSideHelpers({
    router: appRouter,
    // skip ip, origin & host checks in SSR trpc pre-caching requests
    ctx: await createTRPCContext({ isSSR: true }),
    transformer: superjson, // optional - adds superjson serialization
  });
  const id = context.params?.deckId;

  if (!id) {
    return { notFound: true };
  }

  // prefetch so jotai can instantly hydrate
  const p = await ssg.deck.getById.fetch({ id });
  // If the data is not found, redirect to a 404 page
  if (!p) {
    return { notFound: true };
  }

  return {
    props: {
      deckId: id,
      trpcState: ssg.dehydrate(),
    },
    // per 1 day - just in case we make a mistake in a decklist and need to change it and have it get synced without a release
    revalidate: 86400,
  };
};

export const getStaticPaths = (async () => {
  // don't prerender any static pages
  // (faster builds, but slower initial page load)
  if (env.VERCEL_ENV === "preview") {
    return {
      paths: [],
      fallback: "blocking",
    };
  }

  // Populate precon deckIds
  const decks = await prisma.deck.findMany({
    where: {
      set: {
        not: null,
      },
      user: {
        email: OFFICIAL_SORCERY_TCG_ACCOUNT_EMAIL,
      },
    },
    select: {
      id: true,
    },
  });

  // Get the paths we want to prerender based on our ids
  const paths = decks.map((deck) => ({
    params: { deckId: deck.id },
  }));

  return { paths, fallback: false };
}) satisfies GetStaticPaths;

export { default } from "./Precon";
